import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import dataPrivacyService from '../../../services/dataPrivacyService';

const RetentionPolicyManagement = ({ preferences, storageMetrics, onUpdate }) => {
  const retentionPolicy = preferences?.retentionPolicy || {};
  const retentionOptions = dataPrivacyService?.getRetentionPeriodOptions();

  const handlePeriodChange = (field, value) => {
    onUpdate('retentionPolicy', {
      [field]: value
    });
  };

  const dataTypes = [
    {
      key: 'conversations',
      label: 'Conversations',
      description: 'Agent conversation history and messages',
      icon: 'MessageSquare',
      count: storageMetrics?.conversationCount || 0
    },
    {
      key: 'tasks',
      label: 'Tasks',
      description: 'Task records and completion history',
      icon: 'CheckSquare',
      count: storageMetrics?.taskCount || 0
    },
    {
      key: 'auditLogs',
      label: 'Audit Logs',
      description: 'Security and compliance audit trails',
      icon: 'FileText',
      count: storageMetrics?.auditLogCount || 0
    },
    {
      key: 'analyticsData',
      label: 'Analytics Data',
      description: 'Usage metrics and performance analytics',
      icon: 'BarChart3',
      count: 0
    }
  ];

  const calculateStorageImpact = () => {
    const totalMB = storageMetrics?.totalStorageMB || 0;
    return {
      current: totalMB?.toFixed(2),
      projected: (totalMB * 0.7)?.toFixed(2), // Estimated 30% reduction
      savings: (totalMB * 0.3)?.toFixed(2)
    };
  };

  const storageImpact = calculateStorageImpact();

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Clock" size={20} className="text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-foreground">Retention Policy Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure how long different types of data are retained before automatic deletion
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Retention Period Configuration */}
        <div className="space-y-4">
          {dataTypes?.map((dataType) => (
            <div key={dataType?.key} className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name={dataType?.icon} size={16} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-medium text-foreground">{dataType?.label}</h3>
                    <span className="text-xs text-muted-foreground">
                      {dataType?.count} records
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{dataType?.description}</p>
                  <Select
                    options={retentionOptions}
                    value={retentionPolicy?.[dataType?.key]}
                    onChange={(value) => handlePeriodChange(dataType?.key, value)}
                    placeholder="Select retention period"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Auto-Delete Toggle */}
        <div className="pt-4 border-t border-border">
          <Checkbox
            checked={retentionPolicy?.autoDeleteEnabled}
            onChange={(checked) => handlePeriodChange('autoDeleteEnabled', checked)}
            label="Enable Automatic Deletion"
            description="Automatically delete data when retention period expires (recommended for compliance)"
          />
        </div>

        {/* Storage Optimization Metrics */}
        {storageMetrics && (
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="HardDrive" size={16} className="text-foreground" />
              <h3 className="text-sm font-medium text-foreground">Storage Optimization</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Current Storage</div>
                <div className="text-lg font-bold text-foreground">{storageImpact?.current} MB</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Projected Storage</div>
                <div className="text-lg font-bold text-blue-600">{storageImpact?.projected} MB</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Estimated Savings</div>
                <div className="text-lg font-bold text-green-600">{storageImpact?.savings} MB</div>
              </div>
            </div>
          </div>
        )}

        {/* Compliance Notice */}
        <div className="bg-muted/50 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium mb-1">Data Retention Compliance</p>
            <p>
              Retention policies help comply with GDPR Article 5 (storage limitation) and CCPA requirements. 
              Audit logs are recommended to be retained for at least 2 years for compliance purposes. 
              Deleted data cannot be recovered after the retention period expires.
            </p>
          </div>
        </div>

        {/* Warning Notice */}
        {retentionPolicy?.autoDeleteEnabled && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertTriangle" size={16} className="text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-yellow-800">
              <p className="font-medium mb-1">Automatic Deletion Enabled</p>
              <p>
                Data will be permanently deleted when retention periods expire. This action is irreversible. 
                Ensure your retention periods comply with your organization's data retention policies and legal requirements.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetentionPolicyManagement;
